import React, { useState, useEffect } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { InlineWidget } from "react-calendly";
import { 
  CheckCircle, 
  Shield, 
  Leaf, 
  Award, 
  ArrowRight, 
  Phone, 
  Mail, 
  MapPin,
  Star,
  Users,
  TrendingUp,
  Globe,
  Clock,
  Calendar,
  Home,
  FileText,
  ChevronDown,
  ChevronUp,
  Zap,
  Utensils,
  HardHat
} from 'lucide-react';
import ContactForm from './ContactForm';
import DownloadableResources from './DownloadableResources';
import LiveChat from './LiveChat';
import DogPopup from './DogPopup';
import { 
  FloatingElement, 
  StaggerContainer, 
  StaggerItem, 
  MorphingBackground,
  PulseElement,
  RevealText,
  MagneticElement,
  ParallaxElement 
} from './EnhancedAnimations';

const ISOLandingPage = () => {
  const [activeISO, setActiveISO] = useState('9001');
  const [openFAQ, setOpenFAQ] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [contactMode, setContactMode] = useState('form'); // 'form' or 'booking'
  const [showDog, setShowDog] = useState(false);
  const [dogMessage, setDogMessage] = useState('');
  const { scrollYProgress } = useScroll();
  const backgroundY = useTransform(scrollYProgress, [0, 1], ['0%', '30%']);

  // Listen for custom events from child components
  useEffect(() => {
    const handleSetContactMode = (event) => {
      setContactMode(event.detail);
    };

    const handleShowDogPopup = (event) => {
      const message = event.detail || "I won't be in the call, but I'm always ready to play!";
      showDogPopup(message);
    };

    window.addEventListener('setContactMode', handleSetContactMode);
    window.addEventListener('showDogPopup', handleShowDogPopup);
    return () => {
      window.removeEventListener('setContactMode', handleSetContactMode);
      window.removeEventListener('showDogPopup', handleShowDogPopup);
    };
  }, []);

  // Function to show dog popup
  const showDogPopup = (message) => {
    setDogMessage(message);
    setShowDog(true);
    setTimeout(() => {
      setShowDog(false);
    }, 5000); // Hide after 5 seconds
  };

  const isoStandards = {
    '9001': {
      title: 'ISO 9001',
      subtitle: 'Quality Management Systems',
      description: 'Enhance customer satisfaction and operational efficiency through proven quality management frameworks.',
      benefits: ['Improved customer satisfaction', 'Operational efficiency', 'Process optimization', 'Risk management'],
      image: 'https://images.pexels.com/photos/12324202/pexels-photo-12324202.jpeg',
      color: 'from-blue-600 to-blue-800'
    },
    '14001': {
      title: 'ISO 14001',
      subtitle: 'Environmental Management Systems',
      description: 'Reduce environmental impact while improving business performance and compliance.',
      benefits: ['Environmental compliance', 'Cost reduction', 'Risk mitigation', 'Corporate responsibility'],
      image: 'https://images.pexels.com/photos/10365944/pexels-photo-10365944.jpeg',
      color: 'from-green-600 to-green-800'
    },
    '27001': {
      title: 'ISO 27001',
      subtitle: 'Information Security Management',
      description: 'Protect your organization\'s information assets with comprehensive security frameworks.',
      benefits: ['Data protection', 'Cyber security', 'Compliance assurance', 'Business continuity'],
      image: 'https://images.unsplash.com/photo-1531297484001-80022131f5a1',
      color: 'from-purple-600 to-purple-800'
    },
    '45001': {
      title: 'ISO 45001',
      subtitle: 'Occupational Health & Safety',
      description: 'Create safer workplaces and reduce occupational health and safety risks.',
      benefits: ['Workplace safety', 'Risk reduction', 'Legal compliance', 'Employee wellbeing'],
      image: 'https://images.pexels.com/photos/1216589/pexels-photo-1216589.jpeg',
      color: 'from-orange-600 to-orange-800'
    },
    '22000': {
      title: 'ISO 22000',
      subtitle: 'Food Safety Management',
      description: 'Ensure food safety throughout the entire food chain with comprehensive management systems.',
      benefits: ['Food safety assurance', 'Supply chain control', 'Consumer confidence', 'Regulatory compliance'],
      image: 'https://images.pexels.com/photos/5953752/pexels-photo-5953752.jpeg',
      color: 'from-red-600 to-red-800'
    },
    '50001': {
      title: 'ISO 50001',
      subtitle: 'Energy Management Systems',
      description: 'Improve energy performance, reduce costs, and decrease greenhouse gas emissions.',
      benefits: ['Energy efficiency', 'Cost savings', 'Carbon reduction', 'Performance monitoring'],
      image: 'https://images.pexels.com/photos/356036/pexels-photo-356036.jpeg',
      color: 'from-yellow-600 to-yellow-800'
    }
  };

  const testimonials = [
    {
      name: 'Sarah Johnson',
      role: 'Quality Director',
      company: 'TechCorp Industries',
      content: 'The ISO 9001 implementation transformed our operations. We saw a 40% reduction in defects and significantly improved customer satisfaction.',
      rating: 5
    },
    {
      name: 'Michael Chen',
      role: 'CISO',
      company: 'SecureData Solutions',
      content: 'ISO 27001 implementation gave us the framework we needed to protect our clients\' data. The process was thorough and professional.',
      rating: 5
    },
    {
      name: 'Emma Thompson',
      role: 'Sustainability Manager',
      company: 'GreenTech Manufacturing',
      content: 'Our ISO 14001 implementation helped us reduce waste by 60% while cutting operational costs. Excellent guidance throughout.',
      rating: 5
    }
  ];

  const faqs = [
    {
      question: 'How long does ISO implementation take?',
      answer: 'The timeline varies by organization size and current systems, typically ranging from 6-18 months. We provide a detailed timeline during our initial consultation.'
    },
    {
      question: 'What are the costs involved in ISO implementation?',
      answer: 'Costs depend on organization size, current processes, and chosen standards. We offer customized pricing based on your specific needs and provide a detailed cost breakdown.'
    },
    {
      question: 'Do you provide ongoing support after implementation?',
      answer: 'Yes, we offer comprehensive ongoing support including annual reviews, internal audits, and continuous improvement consulting to maintain and enhance your implementation.'
    },
    {
      question: 'Can we implement multiple ISO standards simultaneously?',
      answer: 'Absolutely! We specialize in integrated management systems that combine multiple ISO standards efficiently, reducing implementation time and costs.'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Fixed Navigation Menu */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-200 shadow-lg">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <div className="flex items-center">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                className="flex items-center gap-2 group"
              >
                <Home className="w-5 h-5 text-blue-600 group-hover:text-blue-800 transition-colors duration-200" />
                <h1 className="text-xl font-bold bg-gradient-to-r from-blue-600 to-blue-800 bg-clip-text text-transparent group-hover:from-blue-700 group-hover:to-blue-900 transition-all duration-200">
                  ISO Excellence
                </h1>
              </motion.button>
            </div>
            
            {/* Navigation Menu */}
            <div className="hidden md:flex items-center space-x-8">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => document.getElementById('iso-standards-section')?.scrollIntoView({ behavior: 'smooth' })}
                className="text-gray-700 hover:text-blue-600 font-medium transition-colors duration-200"
              >
                ISO Standards
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => document.getElementById('contact-section')?.scrollIntoView({ behavior: 'smooth' })}
                className="text-gray-700 hover:text-blue-600 font-medium transition-colors duration-200"
              >
                Contact
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => document.getElementById('faq-section')?.scrollIntoView({ behavior: 'smooth' })}
                className="text-gray-700 hover:text-blue-600 font-medium transition-colors duration-200"
              >
                FAQ
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => document.getElementById('resources-section')?.scrollIntoView({ behavior: 'smooth' })}
                className="text-gray-700 hover:text-blue-600 font-medium transition-colors duration-200"
              >
                Free Resources
              </motion.button>
            </div>

            {/* Mobile Menu Button */}
            <div className="md:hidden">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="text-gray-700 hover:text-blue-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </motion.button>
            </div>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="md:hidden py-4 border-t border-gray-200"
            >
              <div className="flex flex-col space-y-4">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => {
                    document.getElementById('iso-standards-section')?.scrollIntoView({ behavior: 'smooth' });
                    setMobileMenuOpen(false);
                  }}
                  className="text-left text-gray-700 hover:text-blue-600 font-medium transition-colors duration-200"
                >
                  ISO Standards
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => {
                    document.getElementById('contact-section')?.scrollIntoView({ behavior: 'smooth' });
                    setMobileMenuOpen(false);
                  }}
                  className="text-left text-gray-700 hover:text-blue-600 font-medium transition-colors duration-200"
                >
                  Contact
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => {
                    document.getElementById('faq-section')?.scrollIntoView({ behavior: 'smooth' });
                    setMobileMenuOpen(false);
                  }}
                  className="text-left text-gray-700 hover:text-blue-600 font-medium transition-colors duration-200"
                >
                  FAQ
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => {
                    document.getElementById('resources-section')?.scrollIntoView({ behavior: 'smooth' });
                    setMobileMenuOpen(false);
                  }}
                  className="text-left text-gray-700 hover:text-blue-600 font-medium transition-colors duration-200"
                >
                  Free Resources
                </motion.button>
              </div>
            </motion.div>
          )}
        </div>
      </nav>
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-16">
        <ParallaxElement speed={0.5} className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-900/95 to-blue-700/95 z-10"></div>
          <img 
            src="https://images.unsplash.com/photo-1517245386807-bb43f82c33c4" 
            alt="Professional consultation"
            className="w-full h-full object-cover"
          />
        </ParallaxElement>
        
        <div className="relative z-20 max-w-7xl mx-auto px-6 text-center">
          <StaggerContainer>
            <StaggerItem>
              <RevealText 
                text="ISO Standards Implementation"
                className="text-4xl sm:text-5xl lg:text-8xl font-bold mb-6 leading-tight bg-gradient-to-r from-blue-300 to-blue-100 bg-clip-text text-transparent px-4"
              />
            </StaggerItem>
            <StaggerItem>
              <p className="text-lg sm:text-xl lg:text-2xl mb-8 max-w-3xl mx-auto text-blue-100 px-4">
                Transform your organization with world-class ISO 9001, 14001, 27001, 45001, 22000, and 50001 implementation. 
                Expert guidance from consultation to implementation.
              </p>
            </StaggerItem>
            <StaggerItem>
              <div className="flex flex-col sm:flex-row gap-4 justify-center px-4">
                <MagneticElement>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => {
                      setContactMode('form');
                      showDogPopup("I won't read the message but my owner will");
                      document.getElementById('contact-section')?.scrollIntoView({ behavior: 'smooth' });
                    }}
                    className="w-full sm:w-auto bg-gradient-to-r from-blue-600 to-blue-800 hover:from-blue-700 hover:to-blue-900 text-white px-6 sm:px-8 py-4 rounded-2xl text-lg font-semibold flex items-center justify-center gap-2 shadow-2xl backdrop-blur-sm"
                  >
                    <Mail className="w-5 h-5" />
                    Send us a message
                  </motion.button>
                </MagneticElement>
                <MagneticElement>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => {
                      setContactMode('booking');
                      showDogPopup("I won't be in the call, but my master will");
                      document.getElementById('contact-section')?.scrollIntoView({ behavior: 'smooth' });
                    }}
                    className="w-full sm:w-auto bg-white text-blue-600 border-2 border-white px-6 sm:px-8 py-4 rounded-2xl text-lg font-semibold hover:bg-gray-50 transition-all duration-300 flex items-center justify-center gap-2"
                  >
                    <Calendar className="w-5 h-5" />
                    Book a call
                  </motion.button>
                </MagneticElement>
              </div>
            </StaggerItem>
          </StaggerContainer>
        </div>

        {/* Floating Elements */}
        <FloatingElement delay={0} duration={8}>
          <div className="absolute top-1/4 left-10 w-20 h-20 bg-blue-500/20 rounded-full backdrop-blur-sm" />
        </FloatingElement>
        <FloatingElement delay={2} duration={6}>
          <div className="absolute top-1/3 right-20 w-16 h-16 bg-blue-600/20 rounded-full backdrop-blur-sm" />
        </FloatingElement>
        <FloatingElement delay={4} duration={10}>
          <div className="absolute bottom-1/3 left-1/4 w-12 h-12 bg-blue-700/20 rounded-full backdrop-blur-sm" />
        </FloatingElement>

        {/* Scroll Indicator */}
        <PulseElement>
          <motion.div 
            className="absolute bottom-8 left-1/2 transform -translate-x-1/2 text-white"
            animate={{ y: [0, 10, 0] }}
            transition={{ repeat: Infinity, duration: 2 }}
          >
            <ChevronDown className="w-8 h-8" />
          </motion.div>
        </PulseElement>
      </section>

      {/* ISO Standards Section */}
      <section id="iso-standards-section" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16 px-4"
          >
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6 bg-gradient-to-r from-blue-600 to-blue-800 bg-clip-text text-transparent">
              ISO Standards We Specialise In
            </h2>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => {
                  setContactMode('form'); showDogPopup("I won't read the message but my owner will");
                  document.getElementById('contact-section')?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="bg-gradient-to-r from-blue-600 to-blue-800 hover:from-blue-700 hover:to-blue-900 text-white px-6 py-3 rounded-xl font-semibold flex items-center justify-center gap-2 shadow-lg"
              >
                <Mail className="w-4 h-4" />
                Send us a message
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => {
                  setContactMode('booking'); showDogPopup("I won't be in the call, but my master will");
                  document.getElementById('contact-section')?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="bg-white border-2 border-blue-600 text-blue-600 hover:bg-gray-50 px-6 py-3 rounded-xl font-semibold flex items-center justify-center gap-2 transition-all duration-300"
              >
                <Calendar className="w-4 h-4" />
                Book a call
              </motion.button>
            </div>
          </motion.div>

          {/* ISO Standards Grid - 2 columns, 3 standards each */}
          <div className="grid md:grid-cols-2 gap-8 mb-12">
            <StaggerContainer>
              {/* First Column - ISO 9001, 14001, 27001 */}
              <div className="space-y-4">
                {Object.entries(isoStandards).slice(0, 3).map(([key, iso], index) => (
                  <StaggerItem key={key} direction="left">
                    <MagneticElement strength={0.1}>
                      <motion.div
                        whileHover={{ scale: 1.02, x: 10 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => setActiveISO(key)}
                        className={`p-4 sm:p-6 rounded-3xl cursor-pointer transition-all duration-500 ${
                          activeISO === key 
                            ? `bg-gradient-to-r ${iso.color} text-white shadow-2xl` 
                            : 'bg-gray-50 hover:bg-gray-100 text-gray-800'
                        }`}
                      >
                        <div className="flex items-start gap-3 sm:gap-4">
                          <div className={`p-2 sm:p-3 rounded-2xl ${
                            activeISO === key ? 'bg-white/20' : 'bg-blue-100'
                          }`}>
                            {key === '9001' && <Award className="w-5 h-5 sm:w-6 sm:h-6" />}
                            {key === '14001' && <Leaf className="w-5 h-5 sm:w-6 sm:h-6" />}
                            {key === '27001' && <Shield className="w-5 h-5 sm:w-6 sm:h-6" />}
                          </div>
                          <div className="flex-1">
                            <h3 className="text-xl sm:text-2xl font-bold mb-2">{iso.title}</h3>
                            <p className="text-base sm:text-lg opacity-90 mb-2 sm:mb-3">{iso.subtitle}</p>
                            <p className={`text-sm sm:text-base ${activeISO === key ? 'text-white/80' : 'text-gray-600'}`}>
                              {iso.description}
                            </p>
                          </div>
                        </div>
                      </motion.div>
                    </MagneticElement>
                  </StaggerItem>
                ))}
              </div>
            </StaggerContainer>

            <StaggerContainer>
              {/* Second Column - ISO 45001, 22000, 50001 */}
              <div className="space-y-4">
                {Object.entries(isoStandards).slice(3, 6).map(([key, iso], index) => (
                  <StaggerItem key={key} direction="right">
                    <MagneticElement strength={0.1}>
                      <motion.div
                        whileHover={{ scale: 1.02, x: -10 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => setActiveISO(key)}
                        className={`p-4 sm:p-6 rounded-3xl cursor-pointer transition-all duration-500 ${
                          activeISO === key 
                            ? `bg-gradient-to-r ${iso.color} text-white shadow-2xl` 
                            : 'bg-gray-50 hover:bg-gray-100 text-gray-800'
                        }`}
                      >
                        <div className="flex items-start gap-3 sm:gap-4">
                          <div className={`p-2 sm:p-3 rounded-2xl ${
                            activeISO === key ? 'bg-white/20' : 'bg-blue-100'
                          }`}>
                            {key === '45001' && <HardHat className="w-5 h-5 sm:w-6 sm:h-6" />}
                            {key === '22000' && <Utensils className="w-5 h-5 sm:w-6 sm:h-6" />}
                            {key === '50001' && <Zap className="w-5 h-5 sm:w-6 sm:h-6" />}
                          </div>
                          <div className="flex-1">
                            <h3 className="text-xl sm:text-2xl font-bold mb-2">{iso.title}</h3>
                            <p className="text-base sm:text-lg opacity-90 mb-2 sm:mb-3">{iso.subtitle}</p>
                            <p className={`text-sm sm:text-base ${activeISO === key ? 'text-white/80' : 'text-gray-600'}`}>
                              {iso.description}
                            </p>
                          </div>
                        </div>
                      </motion.div>
                    </MagneticElement>
                  </StaggerItem>
                ))}
              </div>
            </StaggerContainer>
          </div>

          {/* Active ISO Details Widget - Spans full width below the columns */}
          <motion.div
            key={activeISO}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="bg-gradient-to-br from-white to-blue-50 p-8 rounded-3xl shadow-2xl"
          >
            <div className="grid lg:grid-cols-2 gap-8 items-center">
              <div>
                <h3 className="text-3xl font-bold mb-4 text-gray-800">
                  {isoStandards[activeISO].title} Benefits
                </h3>
                <div className="space-y-3">
                  {isoStandards[activeISO].benefits.map((benefit, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="flex items-center gap-3"
                    >
                      <CheckCircle className="w-5 h-5 text-green-500" />
                      <span className="text-gray-700">{benefit}</span>
                    </motion.div>
                  ))}
                </div>
              </div>
              <div>
                <img 
                  src={isoStandards[activeISO].image}
                  alt={isoStandards[activeISO].title}
                  className="w-full h-64 object-cover rounded-2xl"
                />
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Implementation Process */}
      <section className="py-24 bg-gradient-to-br from-blue-50 to-blue-100">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16 px-4"
          >
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6 text-gray-800">
              Our Implementation Process
            </h2>
            <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto mb-8">
              A proven 5-step methodology that ensures successful ISO implementation with minimal disruption to your operations.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => {
                  setContactMode('form'); showDogPopup("I won't read the message but my owner will");
                  document.getElementById('contact-section')?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="bg-gradient-to-r from-blue-600 to-blue-800 hover:from-blue-700 hover:to-blue-900 text-white px-6 py-3 rounded-xl font-semibold flex items-center justify-center gap-2 shadow-lg"
              >
                <Mail className="w-4 h-4" />
                Send us a message
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => {
                  setContactMode('booking'); showDogPopup("I won't be in the call, but my master will");
                  document.getElementById('contact-section')?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="bg-white border-2 border-blue-600 text-blue-600 hover:bg-gray-50 px-6 py-3 rounded-xl font-semibold flex items-center justify-center gap-2 transition-all duration-300"
              >
                <Calendar className="w-4 h-4" />
                Book a call
              </motion.button>
            </div>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6 lg:gap-8">
            {[
              { step: '01', title: 'Assessment', description: 'Comprehensive gap analysis and readiness evaluation', icon: <FileText className="w-8 h-8" /> },
              { step: '02', title: 'Planning', description: 'Customized implementation roadmap and timeline', icon: <Clock className="w-8 h-8" /> },
              { step: '03', title: 'Implementation', description: 'System deployment with expert guidance', icon: <Users className="w-8 h-8" /> },
              { step: '04', title: 'Training', description: 'Comprehensive staff training and capability building', icon: <TrendingUp className="w-8 h-8" /> },
              { step: '05', title: 'Audit', description: 'Internal audit preparation and readiness support', icon: <Award className="w-8 h-8" /> }
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="bg-white p-4 sm:p-6 rounded-3xl shadow-xl mb-4 hover:shadow-2xl transition-all duration-300 h-[260px] sm:h-[280px] flex flex-col">
                  <div className="bg-gradient-to-br from-blue-600 to-blue-800 text-white w-12 h-12 sm:w-16 sm:h-16 rounded-2xl flex items-center justify-center mx-auto mb-3 sm:mb-4">
                    <div className="w-6 h-6 sm:w-8 sm:h-8">{item.icon}</div>
                  </div>
                  <div className="text-xs sm:text-sm font-bold text-blue-600 mb-2">STEP {item.step}</div>
                  <h3 className="text-lg sm:text-xl font-bold mb-2 sm:mb-3 text-gray-800">{item.title}</h3>
                  <p className="text-gray-600 flex-1 flex items-center text-sm sm:text-base">{item.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section id="contact-section" className="py-24 bg-gradient-to-br from-blue-50 to-blue-100">
        <div className="max-w-4xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-5xl font-bold mb-6 text-gray-800">
              Ready to Start Your ISO Investment?
            </h2>
            <p className="text-xl text-gray-600 mb-8">
              Connect with our ISO experts for a personalized consultation and implementation plan.
            </p>
            
            {/* Contact Options */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setContactMode('form')}
                className={`px-6 py-3 rounded-xl font-semibold flex items-center justify-center gap-2 transition-all duration-300 ${
                  contactMode === 'form'
                    ? 'bg-gradient-to-r from-blue-600 to-blue-800 text-white shadow-lg'
                    : 'bg-white text-blue-600 border-2 border-blue-600 hover:bg-blue-50'
                }`}
              >
                <Mail className="w-4 h-4" />
                Send us a Message
              </motion.button>
              
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setContactMode('booking')}
                className={`px-6 py-3 rounded-xl font-semibold flex items-center justify-center gap-2 transition-all duration-300 ${
                  contactMode === 'booking'
                    ? 'bg-gradient-to-r from-blue-600 to-blue-800 text-white shadow-lg'
                    : 'bg-white text-blue-600 border-2 border-blue-600 hover:bg-blue-50'
                }`}
              >
                <Calendar className="w-4 h-4" />
                Book a Consultation Call
              </motion.button>
            </div>
          </motion.div>
          
          {/* Dynamic Content */}
          <motion.div
            key={contactMode}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            {contactMode === 'form' ? (
              <ContactForm />
            ) : (
              <div className="bg-white rounded-3xl shadow-xl overflow-hidden">
                <div className="p-6 bg-gradient-to-r from-blue-600 to-blue-800 text-white text-center">
                  <h3 className="text-2xl font-bold mb-2">Schedule Your Free ISO Consultation</h3>
                  <p className="text-blue-100">Choose a time that works best for you to discuss your ISO implementation needs</p>
                </div>
                <div className="p-4">
                  <InlineWidget
                    url="https://calendly.com/thiswhitesquare-info"
                    styles={{
                      height: '700px',
                      width: '100%'
                    }}
                    prefill={{
                      customAnswers: {
                        a1: 'ISO Consultation',
                        a2: 'Website Contact'
                      }
                    }}
                    utm={{
                      utmSource: 'website',
                      utmMedium: 'contact-section',
                      utmCampaign: 'iso-consultation'
                    }}
                  />
                </div>
              </div>
            )}
          </motion.div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq-section" className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16 px-4"
          >
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6 text-gray-800">Frequently Asked Questions</h2>
            <p className="text-lg sm:text-xl text-gray-600 mb-8">
              Get answers to common questions about ISO implementation.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => {
                  setContactMode('form'); showDogPopup("I won't read the message but my owner will");
                  document.getElementById('contact-section')?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="bg-gradient-to-r from-blue-600 to-blue-800 hover:from-blue-700 hover:to-blue-900 text-white px-6 py-3 rounded-xl font-semibold flex items-center justify-center gap-2 shadow-lg"
              >
                <Mail className="w-4 h-4" />
                Send us a message
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => {
                  setContactMode('booking'); showDogPopup("I won't be in the call, but my master will");
                  document.getElementById('contact-section')?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="bg-white border-2 border-blue-600 text-blue-600 hover:bg-gray-50 px-6 py-3 rounded-xl font-semibold flex items-center justify-center gap-2 transition-all duration-300"
              >
                <Calendar className="w-4 h-4" />
                Book a call
              </motion.button>
            </div>
          </motion.div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-gray-50 rounded-2xl overflow-hidden"
              >
                <button
                  onClick={() => setOpenFAQ(openFAQ === index ? null : index)}
                  className="w-full p-6 text-left flex justify-between items-center hover:bg-gray-100 transition-colors"
                >
                  <h3 className="text-lg font-semibold text-gray-800">{faq.question}</h3>
                  {openFAQ === index ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                </button>
                {openFAQ === index && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="px-6 pb-6"
                  >
                    <p className="text-gray-600">{faq.answer}</p>
                  </motion.div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Downloadable Resources */}
      <DownloadableResources />

      {/* Testimonials */}
      <section className="py-24 bg-gradient-to-br from-gray-900 to-blue-900 text-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-5xl font-bold mb-6">What Our Clients Say</h2>
            <p className="text-xl text-blue-200 max-w-3xl mx-auto mb-8">
              Don't just take our word for it. Here's what industry leaders say about our ISO implementation services.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => {
                  setContactMode('form'); showDogPopup("I won't read the message but my owner will");
                  document.getElementById('contact-section')?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="bg-gradient-to-r from-blue-600 to-blue-800 hover:from-blue-700 hover:to-blue-900 text-white px-6 py-3 rounded-xl font-semibold flex items-center justify-center gap-2 shadow-lg hover:shadow-xl transition-all"
              >
                <Mail className="w-4 h-4" />
                Send us a message
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => {
                  setContactMode('booking'); showDogPopup("I won't be in the call, but my master will");
                  document.getElementById('contact-section')?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="bg-white border-2 border-blue-600 text-blue-600 hover:bg-gray-50 px-6 py-3 rounded-xl font-semibold flex items-center justify-center gap-2 transition-all duration-300"
              >
                <Calendar className="w-4 h-4" />
                Book a call
              </motion.button>
            </div>
          </motion.div>

          <div className="grid lg:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.2 }}
                viewport={{ once: true }}
                className="bg-white/10 backdrop-blur-sm p-8 rounded-3xl border border-white/20"
              >
                <div className="flex mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                  ))}
                </div>
                <p className="text-white/90 mb-6 text-lg">"{testimonial.content}"</p>
                <div>
                  <h4 className="font-bold text-white">{testimonial.name}</h4>
                  <p className="text-blue-200">{testimonial.role}</p>
                  <p className="text-blue-300 text-sm">{testimonial.company}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-4 gap-12">
            <div>
              <h3 className="text-2xl font-bold mb-6 bg-gradient-to-r from-blue-400 to-blue-600 bg-clip-text text-transparent">
                ISO Excellence
              </h3>
              <p className="text-gray-300 mb-6">
                Your trusted partner for ISO standards implementation. 
                Transform your organization with proven methodologies.
              </p>
              <div className="flex space-x-4">
                <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center">
                  <Globe className="w-5 h-5" />
                </div>
              </div>
            </div>
            
            <div>
              <h4 className="text-lg font-semibold mb-6">Services</h4>
              <ul className="space-y-3 text-gray-300">
                <li>ISO 9001 Implementation</li>
                <li>ISO 14001 Implementation</li>
                <li>ISO 27001 Security</li>
                <li>Gap Analysis</li>
                <li>Training Programs</li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-lg font-semibold mb-6">Industries</h4>
              <ul className="space-y-3 text-gray-300">
                <li>Manufacturing</li>
                <li>Healthcare</li>
                <li>Technology</li>
                <li>Finance</li>
                <li>Education</li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-lg font-semibold mb-6">Contact</h4>
              <div className="space-y-4 text-gray-300">
                <div className="flex items-center gap-3">
                  <Phone className="w-5 h-5" />
                  <span>+44 01908 887 087</span>
                </div>
                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5" />
                  <span>info@thiswhitesquare.com</span>
                </div>
                <div className="flex items-center gap-3">
                  <MapPin className="w-5 h-5" />
                  <span>Unity Place, Milton Keynes, MK9 1UP, UK</span>
                </div>
              </div>
            </div>
          </div>
          
          <div className="border-t border-gray-800 mt-12 pt-8 text-center text-gray-400">
            <p>&copy; 2024 ISO Excellence. All rights reserved. | Privacy Policy | Terms of Service</p>
          </div>
        </div>
      </footer>

      {/* Live Chat Component */}
      <LiveChat />
      
      {/* Dog Popup Component */}
      <DogPopup isVisible={showDog} onClose={() => setShowDog(false)} message={dogMessage} />
    </div>
  );
};

export default ISOLandingPage;